/**
 * Carousel based on CSS scroll snapping.
 *
 * Terminology:
 *
 * - container: the scrolling container (the carousel).
 * - item: a single item in the carousel.
 * - size: width or height, depending on orientation.
 * - gap: space between items (grid/flex gap).
 * - gutter: carousel outside padding.
 * - pos(ition): left or top edge coordinates (x or y), depending on orientation.
 *   Can refer to both the current scroll position in relation to the container
 *   and individual item positions.
 * - pos(ition)End: right or bottom edge coordinates, depending on orientation.
 *   Simply pos + size, i.e. x + width or y + height.
 */

import {
	type KeyboardEventHandler,
	type ReactNode,
	type Ref,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';

import IconButton from 'components/IconButton';
import {
	useDebouncedCallback,
	useEffectOnce,
	useResizeObserver,
	useValueChangeEffect,
} from 'hooks';
import type { HTMLAttributes } from 'types';
import { cn, cnm } from 'utils/classNames';
import { goToElementFocusableOrRoot } from 'utils/dom';
import { empty, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import {
	type AnimationDuration,
	type ControlsSize,
	type Direction,
	getGutterMarginPullClass,
	getGutterPaddingClass,
	getGutterScrollPaddingClass,
	getInlineControlsSizeClass,
	getInlineNextButtonMarginClass,
	getInlinePrevButtonMarginClass,
	getScrollIndexTargetPos,
	getScrollPageTargetPos,
	getScrollPosTargetItem,
	type Gutter,
	type ItemData,
	type Orientation,
	type ScrollCarouselConfig,
	type SnapAlignment,
	useCarouselAnimation,
	useCarouselData,
	useCarouselItemsData,
	useCarouselScrollPosition,
	useOrientationData,
} from './helpers';
import ProgressTracker from './ProgressTracker';

export interface ItemProps extends Partial<ItemData> {
	i: number;
}

export interface CarouselRef {
	goToIndex(index: number, config?: ScrollCarouselConfig): void;
	goToNextPage(config?: ScrollCarouselConfig): void;
	goToPrevPage(config?: ScrollCarouselConfig): void;
}

export interface Props extends Omit<
	HTMLAttributes<HTMLDivElement>,
	'onKeyDown' | 'onScrollEnd'
> {
	alignment?: SnapAlignment;
	animationDuration?: AnimationDuration;
	buttonsClassName?: string;
	carouselClassName?: string;
	className?: string;
	controlsLayout?: 'after' | 'inline' | 'inline-floating';
	controlsSize?: ControlsSize;
	/** Disable scrolling and hide controls. */
	disabled?: boolean;
	gutter?: Gutter;
	hasControls?: boolean;
	/**
	 * If the carousel list itself should be focusable when scrollable.
	 *
	 * Set when the items don't contain focusable elements (e.g. a carousel of
	 * plain images) to allow tabbing keyboard users to control the carousel.
	 * This already happens natively in Firefox and Chrome, Safari being the
	 * usual outlier.
	 *
	 * @see https://chromestatus.com/feature/5231964663578624
	 * @see https://bugs.webkit.org/show_bug.cgi?id=190870
	 */
	hasFocusableList?: boolean;
	hasProgressTracker?: boolean;
	/** Item index to scroll to on initial render. */
	initialIndex?: number;
	itemClassName?: string | ((item: ItemProps) => string | undefined);
	items: (ReactNode | ((item: ItemProps) => ReactNode))[];
	moveFocusOnInitialIndex?: boolean;
	nextButtonClassName?: string;
	nextButtonLabel?: string;
	/** When navigating forwards. */
	onNext?: () => void;
	/** When navigating backwards. */
	onPrevious?: () => void;
	/** When scrolling has stopped. */
	onScrollEnd?: (items: ItemProps[]) => void;
	/**
	 * Called for all carousel changes, including finished sizing from initial
	 * render, item updates and whenever the scroll position changes. Debounced.
	 */
	onUpdate?: (items: ItemProps[]) => void;
	orientation?: Orientation;
	prevButtonClassName?: string;
	prevButtonLabel?: string;
	pullGutter?: boolean;
	ref?: Ref<CarouselRef>;
}

/**
 * Carousel in the form of a container with overflow and CSS scroll snapping.
 *
 * The scrollable container uses grid layout (by default, can be overridden
 * to flex) so use gap to set item spacing.
 */
export default function Carousel({
	alignment,
	animationDuration = 'medium',
	buttonsClassName,
	carouselClassName,
	className,
	controlsLayout = 'after',
	controlsSize = 'sm',
	disabled = false,
	gutter = 'focus-outline',
	hasControls: hasControlsProp = true,
	hasFocusableList = false,
	hasProgressTracker: hasProgressTrackerProp = true,
	// TODO: Change to some kind of focusIndex that's always relevant, not just for
	// initial render? Could possibly remove some manual scrolling from outside.
	initialIndex,
	itemClassName,
	items,
	moveFocusOnInitialIndex,
	nextButtonClassName,
	nextButtonLabel,
	onNext,
	onPrevious,
	onScrollEnd,
	onUpdate,
	// TODO: Bring in more layout styles based on this, maybe add another prop
	// for setting flex or grid? Feels strange to set orientation and also
	// pass a flex-col class.
	orientation = 'horizontal',
	prevButtonClassName,
	prevButtonLabel,
	pullGutter = true,
	ref,
	...attrs
}: Props) {
	const { t } = useI18n();
	const [moveFocusOnDone, setMoveFocusOnDone] = useState(false);
	const carouselRef = useRef<HTMLUListElement | null>(null);
	const itemsRef = useRef<(HTMLLIElement | null)[]>([]);
	const { isHorizontal, isVertical, scrollPosProp } =
		useOrientationData(orientation);
	const { carouselData, updateCarouselSizeData } = useCarouselData(orientation);

	const hasProgressTracker = hasProgressTrackerProp && !disabled;

	const hasControls = hasControlsProp && items.length > 1;
	const hasControlsInline =
		hasControls && is.oneOf(controlsLayout, 'inline', 'inline-floating');
	const hasControlsAfter = hasControls && controlsLayout === 'after';

	const scrollPosThrottleDelay = 250;
	const {
		flushThrottledSetScrollPos,
		scrollPos,
		scrollPosEnd,
		scrollPosRef,
		setScrollPos,
	} = useCarouselScrollPosition({
		containerSize: carouselData.containerSize,
		throttleDelay: scrollPosThrottleDelay,
	});

	const { hasItemsData, itemsData, updateItemsData } = useCarouselItemsData({
		snapAlignment: carouselData.snapAlignment,
		controlButtonSize: carouselData.controlButtonSize,
		gapSize: carouselData.gapSize,
		gutterSize: carouselData.gutterSize,
		itemCount: items.length,
		orientation,
		scrollPos,
		scrollPosEnd,
	});

	const setSizeState = () => {
		if (carouselRef.current) {
			updateCarouselSizeData(carouselRef.current);
		}
		setScrollPos(carouselRef.current?.[scrollPosProp] ?? 0);
		updateItemsData(itemsRef.current);
	};

	const { restartObserver } = useResizeObserver((observer: ResizeObserver) => {
		// Watch the DOM root, the carousel, and each item for size changes.
		// Including the root since things like gaps and margins can change
		// on resize without any of the carousel elements' sizes changing.
		[
			globalThis.document?.scrollingElement,
			carouselRef.current,
			...itemsRef.current,
		]
			.filter(is.truthy)
			.forEach((target) => {
				observer.observe(target);
			});
	}, setSizeState);

	// No mutation observer so check for changes to the number of items.
	useValueChangeEffect(items.length, () => {
		restartObserver();
	});

	// Set initial sizes.
	useEffectOnce(setSizeState);

	// Delay longer than the scroll position throttling to make the debounce
	// useful. Otherwise an update to the scroll position every 200 ms leads
	// to new items data every 200 ms and if a debounce is set to 150 ms it
	// will just run every time.
	const [debouncedOnUpdate] = useDebouncedCallback(
		onUpdate ?? empty.func,
		scrollPosThrottleDelay + 20,
	);
	useValueChangeEffect(itemsData, () => {
		// Checking onUpdate looks wrong when it's the debounced version that's
		// called, but onUpdate can be undefined while the debounced one can not
		// so check the original to avoid pointless calls to the debounced one.
		// Also ensure it's not called with empty items data.
		if (onUpdate && hasItemsData) {
			debouncedOnUpdate(itemsData.map((item, i) => ({ i, ...item })));
		}
	});

	// A faster callback than onUpdate that also only runs on scroll, not when
	// sizing is updated.
	// TODO: use the scrollend event when support is better.
	const [debouncedOnScrollEnd] = useDebouncedCallback(() => {
		if (hasItemsData && onScrollEnd) {
			onScrollEnd(itemsData.map((item, i) => ({ i, ...item })));
		}
	}, 100);

	// The target position is used for the tracker. The regular scrollPos
	// state works but is throttled so the tracker looks a bit laggy when the
	// navigation buttons are used. Instead, use the final animation target
	// position, but only when the buttons are used since that target won't
	// be set when scrolling manually.
	const { animationTargetScrollPos, isAnimating, scrollCarousel } =
		useCarouselAnimation({
			animationDuration,
			// Moving onDone feels slightly laggy since the last few milliseconds
			// of the animation have very little movement. Move when close to done
			// instead.
			onAlmostDone: (targetPos: number) => {
				if (!moveFocusOnDone) {
					return;
				}
				setMoveFocusOnDone(false);
				const targetItem = getScrollPosTargetItem({
					containerSize: carouselData.containerSize,
					itemsData,
					scrollPos: targetPos,
					snapAlignment: carouselData.snapAlignment,
				});
				const targetEl = targetItem ? itemsRef.current[targetItem.index] : null;
				if (targetEl) {
					goToElementFocusableOrRoot(targetEl);
				}
			},
			onDone: () => {
				// Instantly set the final scroll position state when done animating.
				flushThrottledSetScrollPos();
			},
			setElementScrollPos: (to: number) => {
				if (carouselRef.current) {
					carouselRef.current[scrollPosProp] = to;
				}
			},
		});

	const scrollCarouselPage = (
		direction: Direction,
		config?: ScrollCarouselConfig,
	) => {
		if (config?.moveFocusOnDone) {
			setMoveFocusOnDone(true);
		}
		scrollCarousel(
			scrollPosRef.current,
			getScrollPageTargetPos({
				containerSize: carouselData.containerSize,
				direction,
				itemsData,
				// If currently in an animation, use the end target position to allow
				// for faster 'batch navigation', e.g. double click to go two pages
				// forward. Otherwise the second click would animate from something
				// like 'page 1.3'. If not animating, use the current scroll position
				// from ref for accuracy, since the state is throttled.
				scrollPos: isAnimating
					? animationTargetScrollPos
					: scrollPosRef.current,
				snapAlignment: carouselData.snapAlignment,
				totalSize: carouselData.totalSize,
			}),
			config,
		);
	};
	const goToPrevPage = (config?: ScrollCarouselConfig) => {
		if (!hasItemsData || disabled) {
			return;
		}
		scrollCarouselPage('prev', config);
		onPrevious?.();
	};
	const goToNextPage = (config?: ScrollCarouselConfig) => {
		if (!hasItemsData || disabled) {
			return;
		}
		scrollCarouselPage('next', config);
		onNext?.();
	};

	const goToIndex = (index: number, config?: ScrollCarouselConfig) => {
		if (!hasItemsData || disabled) {
			return;
		}
		if (config?.moveFocusOnDone) {
			setMoveFocusOnDone(true);
		}
		const targetPos = getScrollIndexTargetPos({
			containerSize: carouselData.containerSize,
			index,
			itemsData,
			snapAlignment: carouselData.snapAlignment,
		});
		if (targetPos !== undefined) {
			scrollCarousel(scrollPosRef.current, targetPos, config);
		}
	};

	// It's difficult to scroll to an initial index from outside via the
	// imperative handle since items data is not available on first render,
	// would require delay hacks.
	useValueChangeEffect(hasItemsData, () => {
		if (hasItemsData && is.number(initialIndex)) {
			if (moveFocusOnInitialIndex) {
				setMoveFocusOnDone(true);
			}
			goToIndex(initialIndex, { animate: false });
		}
	});

	// Expose a public API for external control.
	useImperativeHandle(ref, () => ({
		goToIndex,
		goToNextPage,
		goToPrevPage,
	}));

	// Scroll when arrow keys are pressed.
	const handleContainerKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
		if (disabled) {
			return;
		}

		const isPrev =
			(isHorizontal && e.key === 'ArrowLeft') ||
			(isVertical && e.key === 'ArrowUp');
		const isNext =
			(isHorizontal && e.key === 'ArrowRight') ||
			(isVertical && e.key === 'ArrowDown');
		if (!isPrev && !isNext) {
			return;
		}

		e.preventDefault();
		if (isPrev) {
			goToPrevPage();
		} else {
			goToNextPage();
		}

		// Don't want to move focus if the target is the carousel itself, but
		// if currently on item 3 and a right press moves the carousel to items
		// 5-8, the current focus would be outside the visible area.
		const isFromItem = is.element(e.target) && Boolean(e.target.closest('li'));
		if (isFromItem) {
			setMoveFocusOnDone(true);
		}
	};

	const isScrollable =
		carouselData.totalSize >
		carouselData.containerSize + carouselData.gutterSize * 2;
	const isAtStart = scrollPos === 0;
	const isAtEnd =
		scrollPosEnd + carouselData.gutterSize * 2 >= carouselData.totalSize;

	const isSnapping = !isAnimating;
	const animationDirection: Direction | null = isAnimating
		? animationTargetScrollPos > scrollPos
			? 'next'
			: 'prev'
		: null;

	const buttonClasses = cn(
		hasControlsInline && [
			'absolute z-5',
			isVertical ? 'left-1/2 -translate-x-1/2' : 'top-1/2 -translate-y-1/2',
			// Pull by half the progress tracker size for perfect item centering.
			hasProgressTracker && (isVertical ? '-ml-1.5' : '-mt-1.5'),
			controlsSize === 'xs' &&
				(isVertical
					? 'after:-inset-x-4 after:-inset-y-2.5'
					: 'after:-inset-x-2.5 after:-inset-y-4'),
			controlsSize !== 'xs' &&
				(isVertical
					? 'after:-inset-x-6 after:-inset-y-2'
					: 'after:-inset-x-2 after:-inset-y-6'),
		],
		buttonsClassName,
	);
	const prevButton = (
		<IconButton
			variant={hasControlsInline ? 'transparent' : 'secondary'}
			icon={isVertical ? 'chevron-up' : 'chevron-left'}
			text={prevButtonLabel || t('general_carousel_previous_page_button_label')}
			size={controlsSize}
			tabIndex={-1}
			aria-hidden="true"
			disabled={isAtStart || !isScrollable}
			hidden={disabled}
			onClick={() => {
				goToPrevPage();
			}}
			className={cnm(
				hasControlsInline && [
					controlsSize === 'xs' && (isVertical ? 'top-1' : 'left-1'),
					controlsSize !== 'xs' && (isVertical ? 'top-2' : 'left-2'),
					getInlinePrevButtonMarginClass({ gutter, isVertical }),
				],
				buttonClasses,
				prevButtonClassName,
			)}
		/>
	);
	const nextButton = (
		<IconButton
			variant={hasControlsInline ? 'transparent' : 'secondary'}
			icon={isVertical ? 'chevron-down' : 'chevron-right'}
			text={nextButtonLabel || t('general_carousel_next_page_button_label')}
			size={controlsSize}
			tabIndex={-1}
			aria-hidden="true"
			disabled={isAtEnd || !isScrollable}
			hidden={disabled}
			onClick={() => {
				goToNextPage();
			}}
			className={cnm(
				hasControlsInline && [
					controlsSize === 'xs' && (isVertical ? 'bottom-1' : 'right-1'),
					controlsSize !== 'xs' && (isVertical ? 'bottom-2' : 'right-2'),
					getInlineNextButtonMarginClass({ gutter, isVertical }),
				],
				buttonClasses,
				nextButtonClassName,
			)}
		/>
	);

	return (
		// Keydown bubbles and it's just a bonus.
		// oxlint-disable-next-line jsx-a11y/no-static-element-interactions
		<div
			{...attrs}
			className={cn(
				hasControlsInline && 'relative',
				isVertical &&
					isScrollable &&
					'grid grid-cols-[1fr_min-content] items-center',
				pullGutter && getGutterMarginPullClass({ gutter, isVertical }),
				className,
			)}
			onKeyDown={handleContainerKeyDown}
		>
			{hasControlsInline && prevButton}
			<ul
				ref={carouselRef}
				// oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex
				tabIndex={
					hasFocusableList && isScrollable && items.length > 1 ? 0 : undefined
				}
				className={cnm(
					'no-scrollbar grid',
					isHorizontal &&
						'w-full grid-flow-col overflow-x-auto overflow-y-hidden',
					isVertical && 'h-full overflow-x-hidden overflow-y-auto',
					disabled && 'overflow-hidden',
					getGutterPaddingClass({ gutter, isVertical }),
					getGutterScrollPaddingClass({ gutter, isVertical }),
					isSnapping && [
						'snap-mandatory',
						isHorizontal && 'snap-x',
						isVertical && 'snap-y',
					],
					// The floating version doesn't take up any space so only set size
					// for 'true' inline.
					hasControls &&
						controlsLayout === 'inline' &&
						getInlineControlsSizeClass(controlsSize),
					carouselClassName,
				)}
				onScroll={(e) => {
					setScrollPos(e.currentTarget[scrollPosProp]);
					debouncedOnScrollEnd();
				}}
			>
				{items.map((item, i) => (
					<li
						// Item content is unknown so index is the only way.
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						ref={(el) => {
							itemsRef.current[i] = el;
						}}
						className={cn(
							// Assume this is desired for flex items.
							carouselClassName?.includes('flex') && 'shrink-0',
							isSnapping && [
								(hasControlsInline || alignment === 'center') && 'snap-center',
								!hasControlsInline &&
									alignment !== 'center' &&
									(i === 0 ? 'snap-end' : 'snap-start'),
							],
							is.func(itemClassName)
								? itemClassName({ i, ...itemsData[i] })
								: itemClassName,
							// Margin and padding on the item containers are not handled,
							// spacing should be done with gap.
							'mx-0! px-0!',
						)}
					>
						{is.func(item) ? item({ i, ...itemsData[i] }) : item}
					</li>
				))}
			</ul>
			{hasControlsInline && nextButton}

			{isScrollable && (hasControlsAfter || hasProgressTracker) && (
				<div
					aria-hidden="true"
					className={cnm(
						'flex items-center justify-center',
						hasControlsAfter && [
							'gap-4',
							isVertical ? 'ml-4 flex-col' : 'mt-4',
						],
						!hasControlsAfter && [isVertical ? 'ml-2' : 'mt-2'],
						getGutterPaddingClass({ gutter, isVertical }),
					)}
				>
					{hasControlsAfter && prevButton}
					{hasProgressTracker && (
						<ProgressTracker
							animationDuration={animationDuration}
							containerSize={carouselData.containerSize}
							totalSize={carouselData.totalSize}
							gutter={carouselData.gutterSize}
							orientation={orientation}
							scrollPos={
								animationDirection === 'prev'
									? Math.min(animationTargetScrollPos, scrollPos)
									: animationDirection === 'next'
										? Math.max(animationTargetScrollPos, scrollPos)
										: scrollPos
							}
						/>
					)}
					{hasControlsAfter && nextButton}
				</div>
			)}
		</div>
	);
}
Carousel.displayName = 'Carousel';
